import { Card, BlockStack, InlineStack, IndexTable, Text, TextField, Button, Box, Badge, Banner } from '@shopify/polaris';
import { formatLabelPrice, hasCatalogPriceOverride, priceForSelection } from '#client/components/features/LabelPrinting/utils/productLabels';
import { isLocked, isPrintable, printableTotals } from '#client/components/features/LabelPrinting/utils/styleSearch';

// Mirrors the server-side ceiling in server/utils/labelQuantities.js (MAX_QUANTITY_PER_ITEM):
// keeps a fat-fingered extra digit from becoming a very large print job with no confirmation.
const MAX_QUANTITY_PER_ITEM = 5000;

const HEADINGS = [{ title: 'Variant' }, { title: 'SKU' }, { title: 'UPC' }, { title: 'Price' }, { title: 'On hand' }, { title: 'Labels to print' }];

export default function StyleVariantTable({ variants, counts, onCountChange, skuType, priceSelection, inventoryKnown, locationName, onPrint, isPrinting }) {
    const totals = printableTotals({ variants, counts, skuType, inventoryKnown });

    const rows = variants.map((variant, index) => {
        const locked = isLocked(variant, { inventoryKnown });
        const printable = isPrintable(variant, skuType);
        const disabled = locked || !printable;
        const { amount } = priceForSelection(variant, priceSelection);
        const isCatalogPrice = hasCatalogPriceOverride(amount, variant.defaultPrice);

        return (
            <IndexTable.Row id={variant.sku} key={variant.id} position={index} tone={disabled ? 'subdued' : undefined}>
                <IndexTable.Cell>
                    <Text as="span">{variant.title}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span">{skuType === 'fnsku' ? variant.amazonSku || 'No FNSKU' : variant.sku}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span" tone={!printable ? 'critical' : undefined}>
                        {skuType === 'fnsku' ? variant.amazonSku || 'No FNSKU - cannot print' : variant.barcode || 'No barcode - cannot print'}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <InlineStack gap="150" blockAlign="center" wrap={false}>
                        <Text as="span" fontWeight={isCatalogPrice ? 'semibold' : undefined}>
                            {formatLabelPrice(amount)}
                        </Text>
                        {isCatalogPrice && <Badge tone="info">Catalog</Badge>}
                    </InlineStack>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span">{Number.isFinite(variant.onHand) ? variant.onHand : '—'}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <TextField
                        label="Labels to print"
                        labelHidden
                        type="number"
                        min="0"
                        max={MAX_QUANTITY_PER_ITEM}
                        autoComplete="off"
                        disabled={disabled}
                        value={String(counts[variant.sku] ?? 0)}
                        onChange={(value) => onCountChange(variant.sku, value)}
                        id={`label-count-${variant.sku}`}
                    />
                </IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    return (
        <Card>
            <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="050">
                        <Text as="h2" variant="headingMd">
                            Variants and label quantities
                        </Text>
                        <Text as="span" variant="bodySm" tone="subdued">
                            One label per unit on hand{locationName ? ` at ${locationName}` : ''}.
                        </Text>
                    </BlockStack>
                    <Text as="span" variant="bodySm" tone="subdued">
                        {`${totals.variantsToPrint} variants to print • ${totals.labels} labels`}
                    </Text>
                </InlineStack>

                {!inventoryKnown && <Banner tone="warning">On-hand could not be loaded for this store. Enter label quantities manually, or try again.</Banner>}

                <IndexTable resourceName={{ singular: 'variant', plural: 'variants' }} itemCount={variants.length} headings={HEADINGS} selectable={false}>
                    {rows}
                </IndexTable>

                <InlineStack align="space-between" blockAlign="center">
                    <Text as="span" variant="bodySm" tone="subdued">
                        Variants with zero or negative on-hand inventory receive no labels.
                    </Text>
                    <Box>
                        <Button variant="primary" disabled={totals.labels === 0} loading={isPrinting} onClick={onPrint}>
                            {`Print ${totals.labels} Labels`}
                        </Button>
                    </Box>
                </InlineStack>
            </BlockStack>
        </Card>
    );
}
